import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, Tabs, TabTitleText } from "@patternfly/react-core";
import { useTranslation } from "react-i18next";

import { appConfigSelectors } from "../../store/appConfig/appConfigSlice";
import { SupportedModelType } from "../../store/appConfig/appDeploymentInfoTypes";
import { appStatusSelectors, currentAppModelTypeSet } from "../../store/appStatus/appStatusSlice";
import { ModelType } from "../../store/global-types/customization-types";
import { selectIntersectionOfSupportedModelTypes } from "../../store/configurationTarget/configurationTargetSelectors";
import { TabKey, getNextActiveTab } from "../../util/tabbed-supported-model-types-utils";

import './tabbed-supported-model-types.css';

export type ModelTypeContent = { modelType: ModelType, content: JSX.Element };

interface TabbedSupportedModelTypesProps {
    content: ModelTypeContent[],
    hasNoBorderBottom?: boolean | undefined,
    /** True if switching tabs should also change current active model type, false otherwise. */
    enableChangeCurrentModelType?: boolean | undefined,
}

/** Helper for getting correct content from props for given model type, or null if the content wasn't specified. */
const getContent = (modelType: ModelType, content: ModelTypeContent[]) => {
    const matchingContent = content.find(c => c.modelType === modelType);
    return matchingContent?.content || null;
}

/** Component for showing content in a tabbed interface based on enabled supported model types, or
 * hiding the tabs UI if only one model type is supported. Also defaults to opening a tab that matches
 * the latest model type that was interacted with. */
const TabbedSupportedModelTypes = (props: TabbedSupportedModelTypesProps) => {

    const { content, hasNoBorderBottom } = props;
    const enableChangeCurrentModelType = props.enableChangeCurrentModelType || false;

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [activeTabKey, setActiveTabKey] = useState<TabKey | undefined>(undefined);

    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const supportedModelTypes = useMemo(() => deploymentInfo?.supportedModelTypes || [], [deploymentInfo]);
    const currentModelType = useSelector(appStatusSelectors.selectCurrentAppModelType);

    const useTabs = supportedModelTypes.length > 1;

    const intersectionOfSupportedModelTypes = useSelector(selectIntersectionOfSupportedModelTypes);

    const isContourConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Contour) || false, [intersectionOfSupportedModelTypes]);
    const isDoseConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Dose) || false, [intersectionOfSupportedModelTypes]);
    const isImageConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Image) || false, [intersectionOfSupportedModelTypes]);
    const isAdaptConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Adapt) || false, [intersectionOfSupportedModelTypes]);

    const handleTabClick = useCallback((_: unknown, tabIndex: string | number) => {
        if (typeof tabIndex === 'string' && Object.values(TabKey).includes(tabIndex as TabKey)) {
            setActiveTabKey(tabIndex as TabKey);
            if (enableChangeCurrentModelType) {

                const tabKeyToModelTypeMap: Record<TabKey, ModelType> = {
                    [TabKey.Contouring]: ModelType.Contouring,
                    [TabKey.Dose]: ModelType.Dose,
                    [TabKey.Image]: ModelType.Image,
                    [TabKey.Adapt]: ModelType.Adapt,
                };

                const modelType = tabKeyToModelTypeMap[tabIndex as TabKey];
                if (modelType) {
                    dispatch(currentAppModelTypeSet(modelType));
                } else {
                    throw new Error(`Unsupported tab index: ${tabIndex}`);
                }
            }
        } else {
            throw new Error(`Unexpected tab index type: ${typeof tabIndex}. Expected a string matching one of: ${Object.values(TabKey).join(', ')}`);
        }
    }, [dispatch, enableChangeCurrentModelType]);

    useEffect(() => {
        const nextActiveTab = getNextActiveTab({
            currentModelType,
            supportedModelTypes: intersectionOfSupportedModelTypes,
            previousTabKey: activeTabKey
        });

        if (nextActiveTab !== undefined) {
            setActiveTabKey(nextActiveTab);
        }
    }, [currentModelType, intersectionOfSupportedModelTypes, activeTabKey]);

    // Return null if none of the config types are supported
    if (!isContourConfigSupported && !isDoseConfigSupported && !isImageConfigSupported && !isAdaptConfigSupported) {
        return null;
    }

    if (!useTabs) {
        if (isContourConfigSupported) {
            return getContent(ModelType.Contouring, content);
        } else if (isDoseConfigSupported) {
            return getContent(ModelType.Dose, content);
        } else if (isImageConfigSupported) {
            return getContent(ModelType.Image, content);
        } else if (isAdaptConfigSupported) {
            return getContent(ModelType.Adapt, content);
        } else {
            return null;
        }
    }

    return (
        <Tabs
            activeKey={activeTabKey}
            onSelect={handleTabClick}
            hasNoBorderBottom={hasNoBorderBottom}
            className="mv-tabs unselectable"
        >
            {isContourConfigSupported && (
                <Tab title={<TabTitleText>{(t('tab.contour'))}</TabTitleText>} eventKey={TabKey.Contouring}>
                    {getContent(ModelType.Contouring, content)}
                </Tab>)}
            {isDoseConfigSupported && (
                <Tab title={<TabTitleText>{(t('tab.dose'))}</TabTitleText>} eventKey={TabKey.Dose}>
                    {getContent(ModelType.Dose, content)}
                </Tab>)}
            {isImageConfigSupported && (
                <Tab title={<TabTitleText>{(t('tab.image'))}</TabTitleText>} eventKey={TabKey.Image}>
                    {getContent(ModelType.Image, content)}
                </Tab>)}
            {isAdaptConfigSupported && (
                <Tab title={<TabTitleText>{(t('tab.adapt'))}</TabTitleText>} eventKey={TabKey.Adapt}>
                    {getContent(ModelType.Adapt, content)}
                </Tab>)}
        </Tabs>
    );
}

export default TabbedSupportedModelTypes;
